import React, {useMemo} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {IFdFarmAssetType, IFdFarmAssetList} from 'constants/interfaces';
import {ToggleButton, ToggleButtonGroup} from '@material-ui/lab';
import {EAssetCondition, EAssetType} from 'constants/index';

const selectedButtonBg = '#acecce';
const selectedButtonColor = '#013333';

export type Submission = {
  id?: string;
  assetListId?: string;
  assetType?: EAssetType;
  make?: string;
  model?: string;
  capacity?: string;
  year?: number;
  value?: number;
  pricePerUnit?: number;
  description?: string;
  condition?: EAssetCondition | string;
};

const assetCategoryFields = {
  VEHICLE: [
    'make',
    'model',
    'capacity',
    'year',
    'value',
    'description',
    'condition',
  ],
  LAND: ['capacity', 'value', 'description'],
  GENERAL: ['make', 'value', 'description'],
  EQUIPMENT: ['make', 'year', 'value', 'description', 'condition'],
  STOCK_ON_HAND: ['pricePerUnit', 'value', 'description'],
};

const assetConditionOptions = [
  {label: 'NEW', value: 'NEW'},
  {label: 'LIKE NEW', value: 'LIKE_NEW'},
  {label: 'GOOD', value: 'GOOD'},
  {label: 'FAIR', value: 'FAIR'},
  {label: 'POOR', value: 'POOR'},
];

export function FdFarmAssetForm(props: {
  asset?: IFdFarmAssetType;
  allAssets: IFdFarmAssetList[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (asset: Submission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  onDelete?: () => any;
  readonly?: boolean;
}) {
  const initialAssetListItem = useMemo(
    () => props.allAssets.find((a) => a.id == props.asset?.assetListItem?.id),
    [props.allAssets, props.asset],
  );
  const initialCategory = initialAssetListItem?.assetCategory || '';

  const {handleSubmit, control, setValue} = useForm<Submission>({
    defaultValues: {
      id: props.asset?.id,
      assetListId: props.asset?.assetListItem?.id,
      make: props.asset?.make || '',
      model: props.asset?.model || '',
      capacity: props.asset?.capacity || '',
      year: props.asset?.year ? parseInt(props.asset.year) : undefined,
      value: props.asset?.value,
      pricePerUnit: props.asset?.pricePerUnit,
      description: props.asset?.description || '',
      condition: props.asset?.condition || '',
      assetType: EAssetType.ASSET,
    },
  });

  const assetListId = useWatch({control, name: 'assetListId'});
  const assetListItem = useMemo(
    () => props.allAssets.find((a) => a.id == assetListId),
    [props.allAssets, assetListId],
  );
  const assetCategory = assetListItem?.assetCategory || initialCategory;

  const renderFieldsByCategory = () => {
    const fields =
      assetCategoryFields[assetCategory as keyof typeof assetCategoryFields];
    if (!fields) return null;

    return fields.map((field) => {
      switch (field) {
        case 'make':
          return (
            <Grid item xs={12} key="make">
              <Controller
                name="make"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Make"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Make is required'}}
              />
            </Grid>
          );
        case 'model':
          return (
            <Grid item xs={12} key="model">
              <Controller
                name="model"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Model"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Model is required'}}
              />
            </Grid>
          );
        case 'capacity':
          return (
            <Grid item xs={12} key="capacity">
              <Controller
                name="capacity"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label={
                      assetCategory === 'LAND' ? 'Size (Hectares)' : 'Capacity'
                    }
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    type="number"
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Capacity is required'}}
              />
            </Grid>
          );
        case 'year':
          return (
            <Grid item xs={12} key="year">
              <Controller
                name="year"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Year of manufacture"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={(e) =>
                      onChange(parseFloat(e.target.value) || undefined)
                    }
                    type="number"
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Year is required'}}
              />
            </Grid>
          );
        case 'value':
          return (
            <Grid item xs={12} key="value">
              <Controller
                name="value"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Estimated value (Rands)"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={(e) =>
                      onChange(parseFloat(e.target.value) || undefined)
                    }
                    type="number"
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Value is required'}}
              />
            </Grid>
          );
        case 'pricePerUnit':
          return (
            <Grid item xs={12} key="pricePerUnit">
              <Controller
                name="pricePerUnit"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Expected Price Per Unit (Rands)"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={(e) =>
                      onChange(parseFloat(e.target.value) || undefined)
                    }
                    type="number"
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Price per unit is required'}}
              />
            </Grid>
          );
        case 'description':
          return (
            <Grid item xs={12} key="description">
              <Controller
                name="description"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Description is required'}}
              />
            </Grid>
          );
        case 'condition':
          return (
            <Grid item xs={12} key="condition">
              <Controller
                name="condition"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControl fullWidth error={!!error}>
                    <Typography variant="subtitle2" gutterBottom>
                      Condition
                    </Typography>
                    <ToggleButtonGroup
                      value={value || ''}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue) onChange(newValue);
                      }}
                      aria-label="condition"
                      color="primary"
                      size="small"
                      // disabled={props.readonly || props.isLoading}
                    >
                      {assetConditionOptions.map((opt) => (
                        <ToggleButton
                          key={opt.value}
                          value={opt.value}
                          style={{
                            backgroundColor:
                              value === opt.value
                                ? selectedButtonBg
                                : 'inherit',
                            color:
                              value === opt.value
                                ? selectedButtonColor
                                : 'inherit',
                          }}
                        >
                          {opt.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
                rules={{required: 'Condition is required'}}
              />
            </Grid>
          );
        default:
          return null;
      }
    });
  };

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      ...submission,
    });
  };

  return (
    <div className="generic-form-container farm-asset-form">
      <div className="form-container" style={{width: '100%'}}>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Asset Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="assetListId"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControl fullWidth error={!!error}>
                    <Autocomplete
                      options={props.allAssets}
                      getOptionLabel={(option: IFdFarmAssetList) =>
                        option.name || ''
                      }
                      value={props.allAssets.find((a) => a.id == value) || null}
                      onChange={(_, newValue) => {
                        onChange(newValue ? newValue.id : undefined);
                        // Reset fields when asset type changes
                        setValue('make', '');
                        setValue('model', '');
                        setValue('capacity', '');
                        setValue('year', new Date().getFullYear());
                        setValue('value', undefined);
                        setValue('pricePerUnit', undefined);
                        setValue('description', undefined);
                        setValue('condition', undefined);
                      }}
                      disabled={props.readonly || props.isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Type"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error ? error.message : null}
                        />
                      )}
                    />
                  </FormControl>
                )}
                rules={{required: 'Type is required'}}
              />
            </Grid>
            {renderFieldsByCategory()}
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={4}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              {props.asset?.id && props.onDelete && (
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disableElevation
                    color="secondary"
                    disabled={props.isLoading}
                    onClick={props.onDelete}
                    style={{color: '#d32f2f', borderColor: '#d32f2f'}}
                  >
                    Delete
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={props.asset?.id && props.onDelete ? 4 : 6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress color="primary" size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
